import "./index.scss";
import React, {useContext, useMemo, useState} from "react";
import {StateContext} from "../../App";
import {addProfile, deleteProfileLifecycle, updateProfile} from "../../firebaseTools";
import {VIRTUAL_ALL_PROFILE_ID} from "../../tools";
import Cookies from "js-cookie";

const RESERVED_PROFILE_NAME = "ALL";

const ProfileManager = ({onClose}) => {
	const {profiles, setProfiles, profile, setProfile, addError} = useContext(StateContext);
	const [isEditMode, setEditMode] = useState(false);
	const [newProfileName, setNewProfileName] = useState("");
	const [selectedProfileId, setSelectedProfileId] = useState(
		profile?.id && profile.id !== VIRTUAL_ALL_PROFILE_ID ? profile.id : ""
	);
	const [editProfileName, setEditProfileName] = useState(profile?.profileName ?? "");

	const editableProfiles = useMemo(
		() => profiles.filter((item) => item.id !== VIRTUAL_ALL_PROFILE_ID),
		[profiles]
	);

	const selectedProfile = editableProfiles.find((item) => item.id === selectedProfileId) ?? null;

	const createProfile = async () => {
		const profileName = newProfileName.trim().toUpperCase();
		if (!profileName) return;
		if (profileName === RESERVED_PROFILE_NAME) {
			addError(`Profile "${RESERVED_PROFILE_NAME}" is reserved`);
			return;
		}
		if (editableProfiles.some((item) => item.profileName === profileName)) {
			addError(`Profile "${profileName}" already exists`);
			return;
		}

		const response = await addProfile({profileName}, addError);
		if (!response?.id) return;

		const created = {id: response.id, profileName};
		setProfiles((prev) => [...prev, created]);
		setProfile(created);
		Cookies.set("profileId", created.id, {expires: 7, path: "/"});
		onClose();
	};

	const saveProfile = async () => {
		if (!selectedProfile) {
			addError("Please select a profile");
			return;
		}

		const profileName = editProfileName.trim().toUpperCase();
		if (!profileName) {
			addError("Profile name is required");
			return;
		}
		if (profileName === RESERVED_PROFILE_NAME) {
			addError(`Profile "${RESERVED_PROFILE_NAME}" is reserved`);
			return;
		}
		if (editableProfiles.some((item) => item.id !== selectedProfile.id && item.profileName === profileName)) {
			addError(`Profile "${profileName}" already exists`);
			return;
		}

		const updated = await updateProfile(selectedProfile.id, {profileName}, addError);
		if (!updated) return;

		const updatedProfile = {...selectedProfile, profileName};
		setProfiles((prev) => prev.map((item) => item.id === selectedProfile.id ? updatedProfile : item));
		if (profile?.id === selectedProfile.id) {
			setProfile(updatedProfile);
		}
		onClose();
	};

	const removeProfile = async () => {
		if (!selectedProfile) return;
		if (!window.confirm(`Delete profile "${selectedProfile.profileName}" and its scoped data?`)) {
			return;
		}

		const removed = await deleteProfileLifecycle(selectedProfile.id, addError);
		if (!removed) return;

		setProfiles((prev) => {
			const updated = prev.filter((item) => item.id !== selectedProfile.id);
			const nextProfile = updated[0] ?? {id: VIRTUAL_ALL_PROFILE_ID, profileName: "ALL", isVirtual: true};
			setProfile(nextProfile);
			Cookies.set("profileId", nextProfile.id, {expires: 7, path: "/"});
			return updated;
		});
		onClose();
	};

	return (
		<div className="profile-manager-container">
			<div className="profile-manager-content">
				<button className="close-button" onClick={onClose}>×</button>
				<h2>Profile Manager</h2>
				<div className="mode-container" role="tablist" aria-label="Profile manager mode">
					<button
						type="button"
						role="tab"
						aria-selected={!isEditMode}
						className={`mode-button ${!isEditMode ? "active-mode" : ""}`}
						onClick={() => setEditMode(false)}
					>
						Add
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={isEditMode}
						className={`mode-button ${isEditMode ? "active-mode" : ""}`}
						onClick={() => setEditMode(true)}
					>
						Edit
					</button>
				</div>

				{!isEditMode ? (
					<input
						type="text"
						placeholder="Profile Name"
						value={newProfileName}
						maxLength={32}
						onChange={(e) => setNewProfileName(e.target.value)}
					/>
				) : (
					<>
						<select
							value={selectedProfileId}
							onChange={(e) => {
								setSelectedProfileId(e.target.value);
								const selected = editableProfiles.find((item) => item.id === e.target.value);
								setEditProfileName(selected?.profileName ?? "");
							}}
						>
							<option value="" disabled>Select profile</option>
							{editableProfiles.map((item) => (
								<option key={item.id} value={item.id}>
									{item.profileName}
								</option>
							))}
						</select>
						<input
							type="text"
							value={editProfileName}
							maxLength={32}
							onChange={(e) => setEditProfileName(e.target.value)}
						/>
					</>
				)}

				<div className="action-container">
					<button
						type="button"
						className="primary"
						onClick={() => isEditMode ? saveProfile() : createProfile()}
					>
						Save
					</button>
					{isEditMode && (
						<button
							type="button"
							className="danger"
							onClick={removeProfile}
							disabled={!selectedProfile}
						>
							Delete
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default ProfileManager;
